import { useEffect } from 'react';
import { activeSite } from './sites';

const DEFAULT_IMAGE = '/website/img/home-hero.jpg';

export interface PageMetaOptions {
  /** Vorschaubild für Social-Netzwerke, seitenrelativ (`/website/img/…`). */
  image?: string;
  /** `article` auf Beitragsseiten, sonst `website`. */
  type?: 'website' | 'article';
  /** Seite aus dem Suchindex halten — für 404 und Fehlerzustände. */
  noindex?: boolean;
  /**
   * Kanonischer Pfad, falls er vom aufgerufenen abweicht. Standard ist der
   * aktuelle Pfad ohne Query — damit `?thema=…` an den CTA-Links keine
   * Dubletten der Kontaktseite in den Index bringt.
   */
  canonicalPath?: string;
  /**
   * `title` unverändert als Seitentitel verwenden, statt „ | Seniorenbetreuung
   * Antczak" anzuhängen.
   *
   * Google zeigt nur rund 60 Zeichen; der Zusatz verbraucht davon 28 für einen
   * Namen, unter dem die Seite ohnehin auf Platz 1 steht. Auf den Seiten, die um
   * Sachbegriffe konkurrieren, ist der Platz für „Gifhorn" oder
   * „24-Stunden-Betreuung" besser investiert.
   */
  standaloneTitle?: boolean;
  /**
   * Das Hero-Bild der Seite vorab laden (`<link rel="preload" as="image">`).
   *
   * Das Bild ist auf den Leistungsseiten das größte Element und damit das,
   * woran Google die Ladezeit misst. Aus dem vorgerenderten `<head>` fordert
   * der Browser es an, bevor er den Seitenkörper gelesen hat. Angegeben wird
   * die WebP-Fassung — wer Preload versteht, versteht auch WebP.
   */
  preloadImage?: string;
}

/**
 * Seitenrelativen Pfad zur absoluten URL machen; Absolutes bleibt unberührt.
 *
 * Die Grundadresse kommt aus dem gerade geltenden Auftritt und nicht mehr aus
 * einer Konstanten: Canonical und `og:url` einer Seite auf miteinander38.de
 * müssen auf miteinander38.de zeigen. Stünde dort die Adresse des anderen
 * Auftritts, erklärte die Landingpage sich selbst zur Dublette der Kundenseite
 * — und verschwände aus dem Index, ohne dass irgendwo ein Fehler aufträte.
 */
const absolute = (pathOrUrl: string): string =>
  pathOrUrl.startsWith('http') ? pathOrUrl : `${activeSite().url}${pathOrUrl}`;

/**
 * Setzt Titel, Description, Canonical und Social-Vorschau einer öffentlichen
 * Seite.
 *
 * Alle Tags stehen bereits in `public/index.html` und werden hier nur
 * überschrieben. So enthält das ausgelieferte HTML auch ohne ausgeführtes
 * JavaScript sinnvolle Startwerte, und beim Verlassen der Seite lässt sich
 * genau dieser Zustand wiederherstellen.
 */
export function usePageMeta(
  title: string,
  description: string,
  options: PageMetaOptions = {},
): void {
  const { image, type = 'website', noindex = false, canonicalPath, standaloneTitle, preloadImage } = options;

  useEffect(() => {
    // Der Marken-Suffix ist der Name des geltenden Auftritts. Auf der
    // Landingpage steht dort „Miteinander38", nicht der Firmenname — die
    // Kampagne ist genau der Name, unter dem die Radiohörer suchen.
    const suffix = activeSite().name;
    let fullTitle = suffix;
    if (title) fullTitle = standaloneTitle ? title : `${title} | ${suffix}`;
    const canonical = absolute(canonicalPath ?? window.location.pathname);

    /** Attribute setzen und dabei die Rücksetz-Funktionen einsammeln. */
    const restores: Array<() => void> = [];
    const set = (selector: string, attribute: string, value: string) => {
      const tag = document.querySelector<HTMLElement>(selector);
      if (!tag) return;
      const previous = tag.getAttribute(attribute);
      tag.setAttribute(attribute, value);
      restores.push(() => {
        if (previous === null) tag.removeAttribute(attribute);
        else tag.setAttribute(attribute, previous);
      });
    };

    const previousTitle = document.title;
    document.title = fullTitle;
    restores.push(() => {
      document.title = previousTitle;
    });

    set('meta[name="description"]', 'content', description);
    set('meta[name="robots"]', 'content', noindex ? 'noindex, follow' : 'index, follow');
    set('link[rel="canonical"]', 'href', canonical);
    set('meta[property="og:title"]', 'content', fullTitle);
    set('meta[property="og:description"]', 'content', description);
    set('meta[property="og:url"]', 'content', canonical);
    set('meta[property="og:type"]', 'content', type);
    // Der Name des Auftritts, nicht der der Firma: In der Vorschau bei WhatsApp
    // und Facebook steht diese Zeile über dem Titel, und wer einen Link von
    // miteinander38.de weiterschickt, hat den Namen aus dem Radio im Ohr.
    set('meta[property="og:site_name"]', 'content', suffix);
    set('meta[property="og:image"]', 'content', absolute(image ?? DEFAULT_IMAGE));

    if (preloadImage) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = preloadImage;
      if (/\.webp$/i.test(preloadImage)) link.type = 'image/webp';
      link.setAttribute('fetchpriority', 'high');
      link.dataset.pageMeta = 'preload';
      document.head.appendChild(link);
      restores.push(() => link.remove());
    }

    return () => restores.forEach((restore) => restore());
  }, [title, description, image, type, noindex, canonicalPath, standaloneTitle, preloadImage]);
}

export default usePageMeta;
